import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"
import { fieldToken, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"
import { syncGateReason } from "../lib/gate_reason"

export default class extends Controller {
  static targets = ["checkbox", "hiddenInputs", "pillList", "saveButton"]
  static values = { url: String }

  // BUTTON-9: the modal's Save is enabled only while the ticked agents differ
  // from the set that was loaded (or last saved — a save does not reload).
  connect() {
    this.baseline = this.selectionFingerprint()
    this.syncSaveState()
  }

  checkboxTargetConnected(checkbox) {
    checkbox.addEventListener("change", this.onCheckboxChange)
  }

  checkboxTargetDisconnected(checkbox) {
    checkbox.removeEventListener("change", this.onCheckboxChange)
  }

  onCheckboxChange = () => this.syncSaveState()

  selectionFingerprint() {
    return this.checkboxTargets.map(fieldToken).join("|")
  }

  isDirty() {
    return this.selectionFingerprint() !== this.baseline
  }

  syncSaveState() {
    if (!this.hasSaveButtonTarget) return
    syncGateReason(this.saveButtonTarget, { ready: this.isDirty(), reason: SAVE_UNCHANGED_REASON })
  }

  async save(event) {
    event.preventDefault()
    if (!this.isDirty()) return
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true
      this.saveButtonTarget.setAttribute("aria-busy", "true")
    }

    const body = new FormData()
    this.selectedIds().forEach((id) => body.append("notify_agent_ids[]", id))

    try {
      const response = await patch(this.urlValue, { body, responseKind: "json" })
      if (!response.ok) {
        this.showToast("Failed to update notify agents. Please try again.", "bg-danger")
        return
      }

      const json = await response.json
      this.renderPills(json.agents || [])
      this.renderHiddenInputs(json.notify_agent_ids || [])
      this.baseline = this.selectionFingerprint()
      this.closeModal()
      this.showToast(json.message || "Notify agents saved.", "bg-success")
    } catch {
      this.showToast("Failed to update notify agents. Please try again.", "bg-danger")
    } finally {
      if (this.hasSaveButtonTarget) this.saveButtonTarget.removeAttribute("aria-busy")
      this.syncSaveState()
    }
  }

  selectedIds() {
    return this.checkboxTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value)
  }

  renderPills(agents) {
    if (!this.hasPillListTarget) return

    this.pillListTarget.innerHTML = ""
    if (agents.length === 0) {
      const empty = document.createElement("span")
      empty.className = "gl-text-muted small"
      empty.textContent = "No agents selected"
      this.pillListTarget.appendChild(empty)
      return
    }

    agents.forEach((agent) => {
      const pill = document.createElement("span")
      pill.className = "gl-pill"
      pill.dataset.notifyAgentId = agent.id
      pill.textContent = agent.name
      this.pillListTarget.appendChild(pill)
    })
  }

  renderHiddenInputs(ids) {
    if (!this.hasHiddenInputsTarget) return

    this.hiddenInputsTarget.innerHTML = ""
    this.hiddenInputsTarget.appendChild(this.hiddenInput(""))
    ids.forEach((id) => this.hiddenInputsTarget.appendChild(this.hiddenInput(id)))
  }

  hiddenInput(value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "support_email_template[notify_agent_ids][]"
    input.value = value
    return input
  }

  closeModal() {
    const modal = this.element.querySelector(".modal")
    const instance = modal && window.bootstrap?.Modal.getInstance(modal)
    if (instance) instance.hide()
  }

  showToast(message, style) {
    if (typeof window.showToast === "function") {
      window.showToast(message, style)
    }
  }
}
